import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { deleteAccount } from '@/api/auth';
import { WebAnalytics } from '@/lib/analytics';
import { EVENT_ACCOUNT_DELETED } from '@/lib/analyticsEvents';
import { error as logError } from '@/utils/logger';

export interface UseDeleteAccountResult {
  isDeleting: boolean;
  /** Soft-deletes the account, then sends the user to /login. Resolves false on failure. */
  confirmDelete: () => Promise<boolean>;
}

export function useDeleteAccount(): UseDeleteAccountResult {
  const navigate = useNavigate();
  const [isDeleting, setIsDeleting] = useState(false);

  const confirmDelete = useCallback(async (): Promise<boolean> => {
    if (isDeleting) return false;
    setIsDeleting(true);
    try {
      await deleteAccount();
      WebAnalytics.track(EVENT_ACCOUNT_DELETED);
      navigate('/login', { replace: true });
      return true;
    } catch (err) {
      logError('[useDeleteAccount] Error deleting account', err);
      toast.error('Could not delete your account. Please try again.');
      setIsDeleting(false);
      return false;
    }
  }, [isDeleting, navigate]);

  return { isDeleting, confirmDelete };
}
